<script lang="ts">
import { computed, defineComponent, inject, onActivated, onDeactivated, PropType, reactive, ref, watch } from "vue";
import Modal from "@/components/Modal.vue";
import formValidierung from "@/businesslogic/formValidierung";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Steps from "@/components/Steps.vue";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import Antragsteller from "@/components/person/Antragsteller.vue";
import Vorschaeden from "@/components/vorschaeden/Vorschaeden.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Zahlungsdaten from "@/components/zahlungsdaten/Zahlungsdaten.vue";
import Vorversicherung from "@/components/vorversicherung/Vorversicherung.vue";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import InfoBox from "@/components/grid/InfoBox.vue";
import HugMitgliedschaft from "@/components/hug/HugMitgliedschaft.vue";
import Sanierungsfragen from "@/components/sanierung/Sanierungsfragen.vue";
import { NavvItem } from "@/model/NavItem";
import IconSprite from "@/components/IconSprite.vue";
import Logger from "js-logger";
import dateUtils from "@/businesslogic/utils/dateUtils";
import sanierungUtils from "@/businesslogic/utils/sanierungUtils";
import ExtraSelbstbeteiligungService from "@/businesslogic/services/ExtraSelbstbeteiligungService";
import DateiUpload from "@/views/vgv/views/DateiUpload.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import VertragsunterlagenDigital from "@/components/abschluss/VertragsunterlagenDigital.vue";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { ProduktLinie } from "@/model/ProduktLinie";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "IhreDatenVGV",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    VertragsunterlagenDigital,
    Ueberschrift,
    DateiUpload,
    HugMitgliedschaft,
    InfoBox,
    Zeile,
    TitelZelle,
    Zelle,
    ValidierungsFehler,
    Antragsteller,
    Vorversicherung,
    Zahlungsdaten,
    Vorschaeden,
    Steps,
    Modal,
    Sanierungsfragen,
    IconSprite,
  },
  props: {
    isMakler: Boolean,
    navItems: Object as PropType<NavvItem[]>,
  },
  setup: function (_props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const zeigeSpinner: any = inject("zeigeSpinner");

    const { formatCurrency } = formatUtils();
    const { dateStringToIsoDate, getNow } = dateUtils();
    const { pruefeSanierung, gebaeudeAelter30, gebaeudeAelter50 } = sanierungUtils();
    const { eq } = dataUtils();
    const fehler = ref({} as any);
    const zeigeFrageHugNachlass = ref(false);
    let komponenteIstAktiv = false;

    const antragstellerRef = ref<InstanceType<typeof Antragsteller>>();
    const zahlungsdatenRef = ref<InstanceType<typeof Zahlungsdaten>>();

    const aelterAls30 = computed(() => {
      return gebaeudeAelter30(dateStringToIsoDate(piniaStore.state.tarifinformationen.versicherungsbeginn));
    });
    const aelterAls50 = computed(() => {
      return gebaeudeAelter50(dateStringToIsoDate(piniaStore.state.tarifinformationen.versicherungsbeginn));
    });

    const flachdach = computed(() => {
      return piniaStore.state.gebaeudedaten.dachvariante === "1";
    });

    const isHaeuslicheGemeinschaft = computed(() => {
      return eq(piniaStore.state.personendaten.mitversichertePerson.artDerPartnerschaft, 2);
    });

    const sanierungsfragenAnzeigenMakler = computed(() => {
      return (
        piniaStore.state.isMakler &&
        aelterAls30.value &&
        flachdach.value &&
        !aelterAls50.value &&
        !piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt
      );
    });

    const sanierungsfragenAnzeigenDirekt = computed(() => {
      return !piniaStore.state.isMakler && aelterAls30.value && (aelterAls50.value || flachdach.value);
    });

    const modalBeitragsaenderung = reactive({
      titel: "Beitragsänderung",
      text: "Durch Ihre Änderung wurde der Beitrag angepasst. Bitte überprüfen Sie den neuen Beitrag.",
      hatSchliessenButton: true,
      schliessenButtonText: "OK",
    });
    let beitragsaenderungDurchSchadenanzahl = false;
    let beitragsaenderungDurchHugMitgliedschaft = false;
    let beitragsaenderungDurchSanierung = false;
    let beitragsaenderungDurchVertragsbeginn = false;

    const regelAnzahlVorschaedenUeberEins = computed(() => {
      return piniaStore.state.tarifinformationen.vorschaedenAnzahl > 1;
    });
    const regelHatVorschaedenMitLw = computed(() => {
      for (const schaden of piniaStore.state.vorschaeden) {
        if (schaden.schadenart.toLowerCase().indexOf("leitungswasser") >= 0) {
          return true;
        }
      }
      return false;
    });
    const regelHatFehlendeVorversicherung = computed(() => {
      return (
        (piniaStore.state.vorversicherung.antragsart == "wechsel" ||
          (piniaStore.state.vorversicherung.antragsart == "neu" && piniaStore.state.vorversicherung.vorhanden)) &&
        !isNaN(new Date(piniaStore.state.vorversicherung.vertragEnde).valueOf()) &&
        new Date(piniaStore.state.vorversicherung.vertragEnde) < new Date(dateUtils().getIsoDateNowPlus(-365)) &&
        piniaStore.state.gebaeudedaten.baujahr != null &&
        piniaStore.state.gebaeudedaten.baujahr < dateUtils().splitDateString(dateUtils().getIsoDateNow()).jahr
      );
    });
    const regelIstGrossesGebaeude = computed(() => {
      const gesamtflaeche =
        (piniaStore.state.gebaeudedaten.wohnflaeche ? piniaStore.state.gebaeudedaten.wohnflaeche : 0) +
        (piniaStore.state.gebaeudedaten.kellerVorhanden && piniaStore.state.gebaeudedaten.wohnflaecheKeller
          ? piniaStore.state.gebaeudedaten.wohnflaecheKeller
          : 0);
      return piniaStore.state.gebaeudedaten.gebaeudesubtyp === "mfh" ? gesamtflaeche > 1500 : gesamtflaeche > 400;
    });
    const regelKuendigungDurchVorversicherer = computed(() => {
      return piniaStore.state.vorversicherung.gekuendigtVon == "yesByInsurer";
    });

    const zeigeHochladenGebaeudefotos = computed(() => {
      return (
        (aelterAls30.value && flachdach.value) ||
        (aelterAls50.value && !piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt) ||
        regelAnzahlVorschaedenUeberEins.value ||
        regelHatVorschaedenMitLw.value ||
        regelHatFehlendeVorversicherung.value ||
        regelIstGrossesGebaeude.value ||
        regelKuendigungDurchVorversicherer.value
      );
    });
    const zeigeHochladenSicherungskasten = computed(() => {
      return (
        (aelterAls50.value && !piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt) ||
        regelAnzahlVorschaedenUeberEins.value ||
        regelHatVorschaedenMitLw.value
      );
    });
    const zeigeHochladenSanitaerbereich = computed(() => {
      return regelHatVorschaedenMitLw.value;
    });

    const zeigeHochladenElementar = computed(() => {
      let hatElementarschaden = false;
      for (const schaden of piniaStore.state.vorschaeden) {
        if (schaden.schadenart.toLowerCase().indexOf("elementar") >= 0) {
          hatElementarschaden = true;
        }
      }

      return (
        piniaStore.state.tarifinformationen.elementar &&
        piniaStore.state.tarifinformationen.vorschaedenAnzahl > 0 &&
        hatElementarschaden
      );
    });

    const zeigeHochladenDenkmalschutz = computed(() => {
      return piniaStore.state.gebaeudedaten.denkmalschutzManuell;
    });

    function hatMitversichertePerson() {
      return (
        piniaStore.state.tarifinformationen.tarifVariante === "993" ||
        piniaStore.state.tarifinformationen.tarifVariante === "994"
      );
    }

    async function eingabenSindValide() {
      const { validiereForm, getFormFehlerAnzahl, getFormFehler, validiereZusammengesetzteNamenLaenge } =
        formValidierung();

      await validiereForm();
      validiereZusammengesetzteNamenLaenge();

      let anstragstellerValide: boolean | undefined = true;
      if (getFormFehlerAnzahl() === 0) {
        anstragstellerValide = await antragstellerRef.value?.pruefen();
      }

      Logger.debug("Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl(), anstragstellerValide, getFormFehler());
      fehler.value = getFormFehler();

      return anstragstellerValide && getFormFehlerAnzahl() === 0;
    }

    function anfragePruefenUndWeiter() {
      piniaStore.state.neuberechnen = true;
      zeigeSpinner.value = true;

      AnfrageService.pruefen(piniaStore.getSessionToken(piniaStore.state), piniaStore.state, "IhreDatenVGV")
        .then((res) => {
          Logger.debug("anfragePruefenUndWeiter(): OK. Ergebnis: ", res.data);
          if (res.data && res.data.aussteuerung !== undefined) {
            piniaStore.state.isDunkelverarbeitung = res.data.aussteuerung === false;
          }

          piniaStore.aktualisiereAussteuerungen(res.data);
        })
        .catch((err) => {
          Logger.debug("anfragePruefenUndWeiter(): Fehler:", err);
        })
        .then(function () {
          zeigeSpinner.value = false;
          emit("next-page", { pageIndex: 3 });
        });
    }

    function nextPage() {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          anfragePruefenUndWeiter();
        }
      });
    }

    watch(
      () => piniaStore.state.tarifinformationen.zahlungsweise,
      (neu, alt) => {
        if (komponenteIstAktiv && neu !== alt) {
          Logger.debug("WATCH (IhreDaten>zahlungsweise): ZW hat sich geändert.");
          if (
            Number(piniaStore.state.tarifinformationen.zahlungsweise) > 1 &&
            piniaStore.state.zahlungsdaten.zahlungsart !== "debit"
          ) {
            Logger.debug("WATCH (IhreDaten>zahlungsweise): Auf Lastschrift umstellen...");
            piniaStore.state.zahlungsdaten.zahlungsart = "debit";
          }
        }
      },
    );

    watch(
      () => piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].gemZahlungsweise,
      (beitragNeu, beitragAlt) => {
        Logger.debug("WATCH (IhreDaten>zahlbeitrag):\nbeitragNeu:" + beitragNeu + "\nbeitragAlt:" + beitragAlt);
        if (
          komponenteIstAktiv &&
          beitragAlt > 0 &&
          beitragNeu !== beitragAlt &&
          (beitragsaenderungDurchSchadenanzahl ||
            beitragsaenderungDurchHugMitgliedschaft ||
            beitragsaenderungDurchVertragsbeginn)
        ) {
          Logger.debug("WATCH (IhreDaten>zahlbeitrag): Beitrag hat sich geändert.");
          if (beitragsaenderungDurchSchadenanzahl) {
            modalBeitragsaenderung.titel = "Vereinbarung Schadenfreiheitsrabatt";
            modalBeitragsaenderung.text = "Durch die Änderung der Schadenanzahl";
            beitragsaenderungDurchSchadenanzahl = false;
          } else if (beitragsaenderungDurchVertragsbeginn) {
            modalBeitragsaenderung.titel = "Neuer Versicherungsbeginn";
            modalBeitragsaenderung.text = "Aufgrund der Verlegung des Versicherungsbeginns";
            beitragsaenderungDurchVertragsbeginn = false;
          } else if (beitragsaenderungDurchHugMitgliedschaft) {
            modalBeitragsaenderung.titel = "Mitgliedschaft Haus & Grund-Verein";
            modalBeitragsaenderung.text = "Durch die Änderung der Mitgliedschaft in einem Haus & Grund-Verein";
            beitragsaenderungDurchHugMitgliedschaft = false;
          }
          modalBeitragsaenderung.text +=
            " ändert sich Ihr Beitrag von " + formatCurrency(beitragAlt) + " auf " + formatCurrency(beitragNeu);
          Logger.debug(modalBeitragsaenderung.text);
          wechsleSichtbarkeit("modalBeitragsaenderung");
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.vorschaedenAnzahl,
      (wertNeu, wertAlt) => {
        if (wertNeu !== wertAlt) {
          beitragsaenderungDurchSchadenanzahl = true;
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.versicherungsbeginn,
      (wertNeu, wertAlt) => {
        const versicherungsbeginn = dateStringToIsoDate(wertNeu);
        Logger.debug(
          "dateStringToIsoDate(wertNeu)",
          getNow(),
          versicherungsbeginn,
          versicherungsbeginn ? versicherungsbeginn >= getNow() : "",
        );

        if (wertNeu !== wertAlt && versicherungsbeginn && versicherungsbeginn >= getNow()) {
          beitragsaenderungDurchVertragsbeginn = true;
          Logger.debug("beitragsaenderungDurchVertragsbeginn", beitragsaenderungDurchVertragsbeginn, wertAlt, wertNeu);
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.hugMitglied,
      (wertNeu, wertAlt) => {
        if (wertNeu !== wertAlt) {
          zeigeFrageHugNachlass.value = piniaStore.state.tarifinformationen.hugMitglied;
          beitragsaenderungDurchHugMitgliedschaft = true;
        }
      },
    );

    watch(
      () => [
        piniaStore.state.tarifinformationen.kernsanierung,
        piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex],
      ],
      (wertNeu, wertAlt) => {
        const neueProduktlinie = wertNeu[1] as ProduktLinie;
        const alteProduktlinie = wertAlt[1] as ProduktLinie;

        const neueKernsanierung = wertNeu[0] as boolean;
        const alteKernsanierung = wertAlt[0] as boolean;

        if (neueKernsanierung !== alteKernsanierung) {
          beitragsaenderungDurchSanierung = true;
        }
        Logger.debug(
          "WATCH (IhreDaten>zahlbeitrag):\nbeitragNeu:" +
            neueProduktlinie.gemZahlungsweise +
            "\nbeitragAlt:" +
            alteProduktlinie.gemZahlungsweise,
        );
        if (
          alteProduktlinie.gemZahlungsweise > 0 &&
          neueProduktlinie.gemZahlungsweise !== alteProduktlinie.gemZahlungsweise &&
          beitragsaenderungDurchSanierung
        ) {
          piniaStore.state.tarifinformationen.beitragVorKernsanierungGemZahlungsweise =
            alteProduktlinie.gemZahlungsweise;
          piniaStore.state.tarifinformationen.beitragVorKernsanierungJaehrlich = alteProduktlinie.jaehrlich;
          beitragsaenderungDurchSanierung = false;
          if (neueProduktlinie.gemZahlungsweise > 25) {
            piniaStore.state.tarifinformationen.anpassungZahlungsweise = false;
          }
        }
      },
    );

    function pruefeZusaetzlicheSelbstbeteiligung() {
      piniaStore.state.tarifinformationen.extraSelbstbeteiligung = 0;
      if (
        ExtraSelbstbeteiligungService.vgvSelbstbeteiligungVorversicherungFehltPruefen(
          piniaStore.state.isMakler,
          piniaStore.state.vorversicherung.vertragEnde,
          piniaStore.state.vorversicherung.vorhanden,
          piniaStore.state.tarifinformationen.versicherungsbeginn,
        )
      ) {
        piniaStore.state.tarifinformationen.extraSelbstbeteiligung = 1000;
      }
    }

    onActivated(() => {
      komponenteIstAktiv = true;
      zeigeFrageHugNachlass.value = piniaStore.state.tarifinformationen.hugMitglied;
      pruefeSanierung(piniaStore.state.tarifinformationen.versicherungsbeginn);
    });

    onDeactivated(() => {
      komponenteIstAktiv = false;
      pruefeSanierung(piniaStore.state.tarifinformationen.versicherungsbeginn);
      pruefeZusaetzlicheSelbstbeteiligung();
    });

    return {
      piniaStore,
      zeigeFrageHugNachlass,
      wechsleSichtbarkeit,
      istSichtbar,
      hatMitversichertePerson,
      nextPage,
      fehler,
      formatCurrency,
      modalBeitragsaenderung,
      isHaeuslicheGemeinschaft,
      aelterAls30,
      aelterAls50,
      antragstellerRef,
      zahlungsdatenRef,
      flachdach,
      sanierungsfragenAnzeigenMakler,
      sanierungsfragenAnzeigenDirekt,
      zeigeHochladenGebaeudefotos,
      zeigeHochladenSicherungskasten,
      zeigeHochladenSanitaerbereich,
      zeigeHochladenElementar,
      zeigeHochladenDenkmalschutz,
    };
  },
});
</script>

<template>
  <Steps v-bind:items="navItems" :active="3" @goto-page="$emit('goto-page', $event)"></Steps>

  <section class="js-step-3">
    <antragsteller :fehler="fehler" ref="antragstellerRef"></antragsteller>

    <vertragsunterlagen-digital :fehler="fehler" />

    <!-- ### Mitversicherter Lebenspartner (MVP) ### -->
    <div v-if="hatMitversichertePerson()">
      <div class="o-grid u-mar-t-1">
        <div class="o-grid__cell o-grid__cell--4-1024 o-grid__cell--12-360">
          <h2 v-if="!piniaStore.state.isMakler" class="u-form-heading">Mitversicherter Lebenspartner</h2>
          <h2 v-else class="u-form-heading">
            Mitversicherte Person<span class="c-notice-icon" @click="wechsleSichtbarkeit('mitversichertePersonInfo')">
              <icon-sprite icon-name="icon-info-i" />
            </span>
          </h2>
        </div>
        <div class="o-grid__cell o-grid__cell--6-1024 o-grid__cell--12-360">
          <info-box id="mitversichertePersonInfo">
            Hier können Sie die Mitversicherung eines in häuslicher Gemeinschaft lebenden volljährigen Partners
            veranlassen. Kinder werden automatisch berücksichtigt und sind nicht separat zu erfassen.
          </info-box>
        </div>
      </div>
      <!-- Art Partnerschaft -->
      <zeile>
        <titel-zelle>Art der Partnerschaft</titel-zelle>
        <zelle>
          <select
            id="typeOfPartnership"
            name="typeOfPartnership"
            v-model.number="piniaStore.state.personendaten.mitversichertePerson.artDerPartnerschaft"
            data-cy="typeOfPartnership"
            class="c-form-control-select"
            :required="hatMitversichertePerson()"
          >
            <option selected value="">Bitte wählen</option>
            <option value="1">Ehe/eingetragene Lebenspartnerschaft</option>
            <option value="2">Häusliche Gemeinschaft</option>
          </select>
          <validierungs-fehler :fehler-text="fehler.typeOfPartnership" :reduzierter-abstand="true" />
        </zelle>
      </zeile>

      <!-- MVP: Vorname und Name -->
      <span v-if="isHaeuslicheGemeinschaft">
        <zeile>
          <titel-zelle>Vorname und Name</titel-zelle>
          <zelle :breite="3">
            <input
              type="text"
              id="partnerFirstname"
              name="partnerFirstname"
              v-model.lazy.trim="piniaStore.state.personendaten.mitversichertePerson.vorname"
              data-cy="partnerFirstname"
              class="c-form-control__input"
              placeholder="Vorname"
              minlength="2"
              maxlength="40"
              :required="isHaeuslicheGemeinschaft"
              :aria-required="isHaeuslicheGemeinschaft"
              aria-label="Vorname des Partners"
              data-fehlermeldung="Bitte geben den Vornamen ein (2-40 Zeichen)."
            />
            <validierungs-fehler :fehler-text="fehler.partnerFirstname" />
          </zelle>
          <zelle :breite="3">
            <input
              type="text"
              id="partnerLastname"
              name="partnerLastname"
              v-model.lazy.trim="piniaStore.state.personendaten.mitversichertePerson.nachname"
              data-cy="partnerLastname"
              class="c-form-control__input"
              placeholder="Name"
              minlength="2"
              maxlength="40"
              :required="isHaeuslicheGemeinschaft"
              :aria-required="isHaeuslicheGemeinschaft"
              aria-label="Nachname des Partners"
              data-parsley-combined-name-length="40"
              data-fehlermeldung="Bitte geben den Nachnamen ein (2-40 Zeichen)."
            />
            <validierungs-fehler :fehler-text="fehler.partnerLastname" />
          </zelle>
        </zeile>

        <zeile>
          <titel-zelle>Geburtsdatum</titel-zelle>
          <zelle :breite="3">
            <input
              type="date"
              id="partnerBirthdate"
              name="partnerBirthdate"
              v-model.lazy.trim="piniaStore.state.personendaten.mitversichertePerson.geburtsdatum"
              data-cy="partnerBirthdate"
              class="c-form-control__input"
              placeholder="TT.MM.JJJJ"
              title="Geburtsdatum im Format TT.MM.JJJJ"
              :required="isHaeuslicheGemeinschaft"
              :aria-required="isHaeuslicheGemeinschaft"
              aria-label="Geburtsdatum im im Format TT.MM.JJJJ"
              data-checkalter18bis100="true"
            />
            <validierungs-fehler :fehler-text="fehler.partnerBirthdate" />
          </zelle>
        </zeile>
      </span>
    </div>

    <zahlungsdaten :fehler="fehler" ref="zahlungsdatenRef" />

    <vorversicherung :fehler="fehler" />

    <vorschaeden :fehler="fehler" />

    <ueberschrift v-if="isMakler" titel="Dateien hochladen" :zeige-info="true" info-box-id="dateienInfo" />
    <zeile v-if="isMakler">
      <titel-zelle>Zur Antragstellung benötigen wir folgende Dokumente von Ihnen:</titel-zelle>
      <zelle>
        <info-box id="dateienInfo">
          Sie können Dokumente bis zu 10 MB hochladen. <br />Zulässige Dateiformate: .jpeg, .jpg, .png, .pdf und .tiff.
        </info-box>
        <datei-upload
          v-if="zeigeHochladenGebaeudefotos"
          id="hochladenGebaeudefotos"
          ueberschrift="Fotos von jeder Gebäudeseite (inkl. Dach)"
        />
        <datei-upload
          v-if="zeigeHochladenSicherungskasten"
          id="hochladenSicherungskasten"
          ueberschrift="Foto vom geöffneten Sicherungskasten"
        />
        <datei-upload
          v-if="zeigeHochladenSanitaerbereich"
          id="hochladenSanitaerbereich"
          ueberschrift="Fotos von Sanitärbereich, Heizungsanlage und Wasserversorgung"
        />
        <datei-upload
          v-if="zeigeHochladenElementar"
          id="hochladenElementar"
          ueberschrift="Fragebogen Elementar"
          ueberschrift-download-link="https://www.gev-versicherung.de/downloads/pdf/AZ-EL-Elementarfragebogen.pdf"
          :fileCount="1"
          dokumentart="Elementarbogen"
        />
        <datei-upload
          v-if="zeigeHochladenDenkmalschutz"
          id="hochladenDenkmalschutz"
          ueberschrift="Fragebogen Denkmalschutz"
          ueberschrift-download-link="https://www.gev-versicherung.de/downloads/pdf/AZ-VG-Denkmalschutzbogen.pdf"
          :fileCount="1"
          dokumentart="FragenbogenDenkmalschutz"
        />
        <datei-upload id="hochladenDateien" ueberschrift="Andere Dokumente hinzufügen" />
      </zelle>
    </zeile>

    <sanierungsfragen
      v-if="sanierungsfragenAnzeigenMakler || sanierungsfragenAnzeigenDirekt"
      :aelterAls50="aelterAls50"
      :fehler="fehler"
    ></sanierungsfragen>

    <hug-mitgliedschaft v-if="zeigeFrageHugNachlass" :fehler="fehler" />

    <!-- Absenden -->
    <zeile>
      <zelle :offset="4" :breite="4">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage()"
          data-cy="submitIhreDaten"
        >
          Zusammenfassung und Abschluss
        </button>
      </zelle>
    </zeile>
  </section>

  <modal
    v-if="istSichtbar('modalBeitragsaenderung')"
    v-bind="modalBeitragsaenderung"
    @closeModal="wechsleSichtbarkeit('modalBeitragsaenderung')"
  />
</template>
